import { Pencil, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/shadcn/button'
import { LoaderGeneral } from '@/components/common/loaders/LoaderGeneral'
import { cn } from '@/lib/utils'
import type { CompanyManagementUser } from '../permissions.types'

interface Props {
  users: CompanyManagementUser[]
  isLoading: boolean
  isError: boolean
  selectedUserId: string | null
  onSelectUser: (user: CompanyManagementUser) => void
  onAddUser: () => void
  onEditUser: (user: CompanyManagementUser) => void
  canCreate: boolean
  canEdit: boolean
}

export function UserListPanel({ users, isLoading, isError, selectedUserId, onSelectUser, onAddUser, onEditUser, canCreate, canEdit }: Props) {
  const { t } = useTranslation('permissions')

  return (
    <div className="flex-1 min-w-0 flex flex-col border-r">
      <div className="px-4 py-3 border-b bg-muted/40 flex items-center justify-between">
        <p className="text-sm font-semibold">{t('panels.users.title')}</p>
        {canCreate && (
          <Button variant="ghost" size="icon" className="size-7" onClick={onAddUser}>
            <Plus className="size-4" />
          </Button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-custom">
        <LoaderGeneral isLoading={isLoading} isError={isError} isEmpty={!users || users.length === 0}>
          {users.map((user) => (
            <div
              key={user.id}
              onClick={() => onSelectUser(user)}
              className={cn(
                'flex items-center gap-1 px-4 py-2.5 text-sm hover:bg-muted transition-colors border-b border-border/50 cursor-pointer group',
                selectedUserId === user.id && 'bg-muted font-medium'
              )}
            >
              <div className="flex-1 min-w-0">
                <div>{user.firstName} {user.lastName}</div>
                <div className="text-xs text-muted-foreground truncate">{user.email}</div>
              </div>
              {canEdit && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-6"
                  onClick={(e) => {
                    e.stopPropagation()
                    onEditUser(user)
                  }}
                >
                  <Pencil className="size-3.5" />
                </Button>
              )}
            </div>
          ))}
        </LoaderGeneral>
      </div>
    </div>
  )
}
